export type CustomerKind = "individual" | "company";
export type ValidationCountry = "PT" | "BR";

export const normalizeEmail = (value: string) => value.trim().toLowerCase();
export const digitsOnly = (value: string) => value.replace(/\D/g, "");

export function isValidEmail(value: string): boolean {
  const email = normalizeEmail(value);
  if (!email || email.length > 254 || /\s/.test(email)) return false;
  const parts = email.split("@");
  if (parts.length !== 2) return false;
  const [local, domain] = parts;
  if (!local || local.length > 64 || !domain || domain.length > 253) return false;
  if (local.startsWith(".") || local.endsWith(".") || local.includes("..")) return false;
  if (!/^[^\s@]+$/.test(local)) return false;
  const labels = domain.split(".");
  return labels.length >= 2 && labels.every((label) =>
    label.length > 0 && label.length <= 63 && /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(label),
  );
}

function modulus11Portuguese(value: string): boolean {
  if (!/^\d{9}$/.test(value) || /^(\d)\1{8}$/.test(value)) return false;
  const total = value.slice(0, 8).split("").reduce((sum, digit, index) =>
    sum + Number(digit) * (9 - index), 0);
  const remainder = total % 11;
  const check = remainder < 2 ? 0 : 11 - remainder;
  return check === Number(value[8]);
}

export const isValidPortugueseTaxId = (value: string) =>
  modulus11Portuguese(digitsOnly(value));

function isValidCpf(value: string): boolean {
  const digits = digitsOnly(value);
  if (!/^\d{11}$/.test(digits) || /^(\d)\1{10}$/.test(digits)) return false;
  const check = (length: number) => {
    const total = digits.slice(0, length).split("").reduce(
      (sum, digit, index) => sum + Number(digit) * (length + 1 - index), 0);
    const remainder = (total * 10) % 11;
    return Number(digits[length]) === (remainder === 10 ? 0 : remainder);
  };
  return check(9) && check(10);
}

function isValidCnpj(value: string): boolean {
  const digits = digitsOnly(value);
  if (!/^\d{14}$/.test(digits) || /^(\d)\1{13}$/.test(digits)) return false;
  const calculate = (base: string, weights: number[]) => {
    const total = base.split("").reduce((sum, digit, index) => sum + Number(digit) * weights[index], 0);
    const remainder = total % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };
  const first = calculate(digits.slice(0, 12), [5,4,3,2,9,8,7,6,5,4,3,2]);
  const second = calculate(`${digits.slice(0, 12)}${first}`, [6,5,4,3,2,9,8,7,6,5,4,3,2]);
  return digits.endsWith(`${first}${second}`);
}

export function isValidTaxIdentifier(value: string, country: ValidationCountry, kind: CustomerKind): boolean {
  if (country === "PT") return isValidPortugueseTaxId(value);
  return kind === "company" ? isValidCnpj(value) : isValidCpf(value);
}

export function formatTaxIdentifier(value: string, country: ValidationCountry, kind: CustomerKind): string {
  const digits = digitsOnly(value);
  if (country === "PT") return digits.slice(0, 9);
  if (kind === "company") return digits.slice(0, 14).replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2}).*/, "$1.$2.$3/$4-$5").replace(/[-/.]$/, "");
  return digits.slice(0, 11).replace(/^(\d{3})(\d{3})(\d{3})(\d{0,2}).*/, "$1.$2.$3-$4").replace(/[-.]$/, "");
}

export function validateCustomerIdentity(input: {
  kind: CustomerKind;
  country: ValidationCountry;
  displayName: string;
  legalName?: string;
  tradeName?: string;
  taxIdentifier?: string;
  email?: string;
}): string[] {
  const errors: string[] = [];
  if (input.displayName.trim().length < 2) errors.push("Indique um nome com pelo menos 2 caracteres.");
  if (input.kind === "company" && (input.legalName ?? "").trim().length < 2) errors.push("Indique o nome legal da empresa.");
  if (input.kind === "company" && (input.tradeName ?? "").trim().length < 2) errors.push("Indique a marca ou nome comercial da empresa.");
  if (input.email && !isValidEmail(input.email)) errors.push("Indique um email completo, por exemplo nome@empresa.pt.");
  if (input.taxIdentifier && !isValidTaxIdentifier(input.taxIdentifier, input.country, input.kind)) {
    errors.push(`Indique um ${input.country === "BR" ? (input.kind === "company" ? "CNPJ" : "CPF") : (input.kind === "company" ? "NIPC" : "NIF")} válido.`);
  }
  return errors;
}
